import React, { useRef } from "react";
import {
  forwardRef,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  type InputProps,
  useMergeRefs,
} from "@chakra-ui/react";
import { FiSearch, FiX } from "react-icons/fi";

export interface SearchFieldProps extends Omit<InputProps, "onChange" | "value"> {
  /** Omit for uncontrolled use (then no clear button is shown). */
  value?: string;
  onChange: (value: string) => void;
  /** Called after the field is cleared (onChange("") is always called). */
  onClear?: () => void;
  placeholder?: string;
  "aria-label": string;
}

/**
 * Pill search input with a Feather search icon and a clear button. Width 100% on base, 280px from md.
 * @example <SearchField value={q} onChange={setQ} placeholder="Search items" aria-label="Search stock items" />
 */
export const SearchField = forwardRef<SearchFieldProps, "input">(
  ({ value, onChange, onClear, placeholder = "Search", w, maxW, ...rest }, ref) => {
    const hasValue = typeof value === "string" && value.length > 0;
    // Internal ref so the clear button can hand focus back to the input before it unmounts itself.
    const inputRef = useRef<HTMLInputElement>(null);
    const mergedRef = useMergeRefs(ref, inputRef);
    return (
      <InputGroup w={w ?? { base: "100%", md: "280px" }} maxW={maxW} size="md">
        <InputLeftElement pointerEvents="none" h="2.75rem" pl={1}>
          <Icon as={FiSearch} boxSize="16px" color="sand.500" aria-hidden />
        </InputLeftElement>
        <Input
          ref={mergedRef}
          type="search"
          variant="filled-premium"
          borderRadius="full"
          h="2.75rem"
          pl={10}
          pr={hasValue ? 10 : 4}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{ "&::-webkit-search-cancel-button": { display: "none" } }}
          {...rest}
        />
        {hasValue && (
          <InputRightElement h="2.75rem" pr={1}>
            <IconButton
              variant="ghost"
              size="xs"
              isRound
              aria-label="Clear search"
              icon={<FiX />}
              onClick={() => {
                onChange("");
                onClear?.();
                inputRef.current?.focus();
              }}
            />
          </InputRightElement>
        )}
      </InputGroup>
    );
  }
);
